import {
  type AssignableRolesResponse,
  AssignableRolesResponseSchema,
  type BulkDeleteResponse,
  BulkDeleteResponseSchema,
  type CreateUserRequest,
  type EligibleManagerList,
  EligibleManagerListSchema,
  type SetUserPermissionsRequest,
  type UpdateUserRequest,
  type UserDetail,
  UserDetailSchema,
  type UserEmailLogListQuery,
  type UserEmailLogListResponse,
  UserEmailLogListResponseSchema,
  type UserHierarchyResponse,
  UserHierarchyResponseSchema,
  type UserListQuery,
  type UserListResponse,
  UserListResponseSchema,
  type UserPermissionsSnapshot,
  UserPermissionsSnapshotSchema,
  type UserRole,
} from '@utazzo/shared';

import { apiClient } from '@/lib/api-client';

/**
 * Admin users API surface. Each function validates the server response with
 * the shared Zod schema so the UI never operates on drifted data.
 */

export const listUsers = async (query: UserListQuery): Promise<UserListResponse> => {
  const { data } = await apiClient.get<unknown>('/users', { params: query });
  return UserListResponseSchema.parse(data);
};

export const getUser = async (id: string): Promise<UserDetail> => {
  const { data } = await apiClient.get<unknown>(`/users/${id}`);
  return UserDetailSchema.parse(data);
};

/** Role options for the user form. ADMIN role is hidden for a FINANCE caller. */
export const listAssignableRoles = async (): Promise<AssignableRolesResponse> => {
  const { data } = await apiClient.get<unknown>('/users/assignable-roles');
  return AssignableRolesResponseSchema.parse(data);
};

export const createUser = async (payload: CreateUserRequest): Promise<UserDetail> => {
  const { data } = await apiClient.post<unknown>('/users', payload);
  return UserDetailSchema.parse(data);
};

export const updateUser = async (id: string, payload: UpdateUserRequest): Promise<UserDetail> => {
  const { data } = await apiClient.patch<unknown>(`/users/${id}`, payload);
  return UserDetailSchema.parse(data);
};

export const deactivateUser = async (id: string): Promise<UserDetail> => {
  const { data } = await apiClient.post<unknown>(`/users/${id}/deactivate`);
  return UserDetailSchema.parse(data);
};

export const reactivateUser = async (id: string): Promise<UserDetail> => {
  const { data } = await apiClient.post<unknown>(`/users/${id}/reactivate`);
  return UserDetailSchema.parse(data);
};

export const sendPasswordResetForUser = async (id: string): Promise<void> => {
  await apiClient.post(`/users/${id}/send-password-reset`);
};

export const deleteUser = async (id: string): Promise<void> => {
  await apiClient.delete(`/users/${id}`);
};

/** Bulk soft-delete. The endpoint also accepts a single id. */
export const bulkDeleteUsers = async (ids: string[]): Promise<BulkDeleteResponse> => {
  const { data } = await apiClient.post<unknown>('/users/bulk-delete', { ids });
  return BulkDeleteResponseSchema.parse(data);
};

/** Bulk activate/deactivate users. */
export const bulkSetUsersStatus = async (
  ids: string[],
  isActive: boolean,
): Promise<{ updated: number; failed: { id: string; error: string }[] }> => {
  const { data } = await apiClient.post<{
    updated: number;
    failed: { id: string; error: string }[];
  }>('/users/bulk-set-status', { ids, isActive });
  return data;
};

/// Reporting-manager candidates for a role - drives the dependent
/// dropdown on the Add/Edit User form. Returns [] for ADMIN.
export const listEligibleManagers = async (role: UserRole): Promise<EligibleManagerList> => {
  const { data } = await apiClient.get<unknown>('/users/eligible-managers', {
    params: { role },
  });
  const parsed = EligibleManagerListSchema.safeParse(data);
  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    const detail = issue ? `${issue.path.join('.')}: ${issue.message}` : 'schema mismatch';
    throw new Error(`Invalid eligible-managers response (${detail})`);
  }
  return parsed.data;
};

/// Org-chart payload for one user (subject + manager chain + 2-deep
/// direct-report tree). Drives the "Organization" dialog.
export const getUserHierarchy = async (id: string): Promise<UserHierarchyResponse> => {
  const { data } = await apiClient.get<unknown>(`/users/${id}/hierarchy`);
  return UserHierarchyResponseSchema.parse(data);
};

/// Email log for one user, paginated newest-first. Drives the "Email
/// log" tab on the admin user-detail page.
export const listUserEmailLogs = async (
  id: string,
  query: UserEmailLogListQuery,
): Promise<UserEmailLogListResponse> => {
  const { data } = await apiClient.get<unknown>(`/users/${id}/email-logs`, { params: query });
  return UserEmailLogListResponseSchema.parse(data);
};

/// Per-user permission snapshot: role baseline + user-specific extras.
/// Drives the "User permissions" tab on /roles.
export const getUserPermissions = async (id: string): Promise<UserPermissionsSnapshot> => {
  const { data } = await apiClient.get<unknown>(`/users/${id}/permissions`);
  return UserPermissionsSnapshotSchema.parse(data);
};

/// Replace the user's EXTRA permission grants in one call. Codes
/// already covered by the role baseline are filtered server-side.
export const setUserPermissions = async (
  id: string,
  payload: SetUserPermissionsRequest,
): Promise<UserPermissionsSnapshot> => {
  const { data } = await apiClient.patch<unknown>(`/users/${id}/permissions`, payload);
  return UserPermissionsSnapshotSchema.parse(data);
};

/// Per-user Marketing Reports field visibility - the catalog keys this user may
/// NOT see on that screen. Drives the "Marketing Reports field visibility"
/// section on the per-user RBAC panel.
export const getUserMarketingFieldVisibility = async (id: string): Promise<string[]> => {
  const { data } = await apiClient.get<{ hidden: string[] }>(
    `/users/${id}/marketing-field-visibility`,
  );
  return Array.isArray(data?.hidden) ? data.hidden : [];
};

export const setUserMarketingFieldVisibility = async (
  id: string,
  hidden: string[],
): Promise<string[]> => {
  const { data } = await apiClient.patch<{ hidden: string[] }>(
    `/users/${id}/marketing-field-visibility`,
    { hidden },
  );
  return Array.isArray(data?.hidden) ? data.hidden : [];
};
